import QuestionHelper from 'components/QuestionHelper'
import { NFT_TYPE } from 'config/constants/nftTypes'
import { useTranslation } from 'contexts/Localization'
import {
  calcPotentialFarmBoostFactor,
  PotentialFarmBoostFactorResult,
} from 'state/farmBoost/calcPotentialFarmBoostFactor'
import useTheme from 'hooks/useTheme'
import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { Flex, Skeleton, Text } from 'vvs-uikit'

export const Divider = styled.div`
  width: 100%;
  height: 0px;
  border-top: 1px solid ${({ theme }) => theme.colors.boostDivider};
`

const Wrapper = styled(Flex)`
  display: flex;
  gap: 12px;
  padding: 18px;
  background: ${({ theme }) => theme.colors.boostBackground};
  border: 1px solid ${({ theme }) => theme.colors.background};
  border-radius: 12px;
  flex: 1;
  flex-direction: column;
  margin: 24px 24px 0 24px;
`

type NftBoostFactorCardProps = {
  currentStakingMoles?: number
  selectedNftIds: string[]
  questionHelperText?: string
  farmId: number
  type?: 'default' | 'minus' | 'plus'
  nftType?: NFT_TYPE
  nftBoostFactor?: number | string
  currentStakingDesc?: string
  currentSelectedDesc?: string
}

const NftBoostFactorCard: React.FC<NftBoostFactorCardProps> = ({
  currentStakingMoles = 0,
  selectedNftIds = [],
  questionHelperText,
  farmId,
  type = 'default',
  nftType = NFT_TYPE.MINER_MOLE,
  nftBoostFactor = 1,
  currentStakingDesc,
  currentSelectedDesc,
}) => {
  const { theme } = useTheme()
  const { t } = useTranslation()

  const [boostFactorData, setBoostFactor] = useState<PotentialFarmBoostFactorResult>(null)
  const isBoostFactorDataLoading = boostFactorData === null

  useEffect(() => {
    let latest = true
    setBoostFactor(null)
    calcPotentialFarmBoostFactor(farmId, {
      nfts: selectedNftIds,
      excludeExistingNfts: true,
    }).then(async (calculatedBoostFactor) => {
      if (!latest) return

      setBoostFactor(calculatedBoostFactor)
    })

    return () => {
      latest = false
    }
  }, [farmId, selectedNftIds])

  return (
    <Wrapper>
      {currentStakingMoles === 0 ? null : (
        <Flex flex={1} justifyContent="space-between">
          <Text fontSize="13px" lineHeight="18px" color={theme.colors.textSubtle}>
            {currentStakingDesc || t('Current staking Moles')}
          </Text>
          <Text fontSize="13px" lineHeight="18px" color={theme.colors.text}>
            {currentStakingMoles}
          </Text>
        </Flex>
      )}
      <Flex flex={1} justifyContent="space-between">
        <Text fontSize="13px" lineHeight="18px" color={theme.colors.textSubtle}>
          {currentSelectedDesc || t('Moles selected to stake')}
        </Text>
        <Text fontSize="13px" lineHeight="18px" color={theme.colors.text}>
          {selectedNftIds?.length - currentStakingMoles > 0 && (
            <>
              {type === 'minus' && '-'}
              {type === 'plus' && '+'}
            </>
          )}
          {selectedNftIds?.length - currentStakingMoles || 0}
        </Text>
      </Flex>
      <Divider />
      <Flex flex={1} justifyContent="space-between" alignItems="center">
        <Flex alignItems="center" gap="8px">
          <Text fontSize="13px" lineHeight="18px" color={theme.colors.textSubtle} textTransform="uppercase">
            {t('total boost factor')}
          </Text>
          <QuestionHelper
            text={
              questionHelperText ||
              t('NFT boost factor will be determined by the rarity level and number of VVS Miner Moles staked.')
            }
            placement="top-start"
            ml="4px"
            mt="4px"
            iconColor={theme.colors.text}
          />
        </Flex>
        {nftType === NFT_TYPE.MINER_MOLE &&
          (isBoostFactorDataLoading ? (
            <Skeleton width="48px" />
          ) : (
            <Text fontSize="20px" lineHeight="32px" color={theme.colors.text}>
              {boostFactorData?.boostFactor}x
            </Text>
          ))}
        {nftType === NFT_TYPE.ARGO && (
          <Text fontSize="20px" lineHeight="32px" color={theme.colors.text}>
            {nftBoostFactor}x
          </Text>
        )}
      </Flex>
    </Wrapper>
  )
}

export default NftBoostFactorCard
